import React from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';

// Auto Camera Position Component – resets to isometric view on every new file
export function AutoCameraPosition({ stlAnalysis, scale }) {
  const { camera, controls } = useThree();

  React.useEffect(() => {
    if (!stlAnalysis?.dimensions) return;

    const dims = stlAnalysis.dimensions;
    const sx = dims.x * scale;
    const sy = dims.y * scale;
    const sz = dims.z * scale;

    const maxDim = Math.max(sx, sy, sz);
    const dist   = maxDim * 3.0;

    camera.up.set(0, 0, 1);
    camera.position.set(0, dist * -1.4, sz / 2 + dist * 0.8);

    const targetZ = sz / 2;
    camera.lookAt(0, 0, targetZ);
    camera.updateProjectionMatrix();

    if (controls) {
      controls.target.set(0, 0, targetZ);
      controls.update();
    }
  }, [stlAnalysis?.id, camera, controls]);

  return null;
}

// Rectangular build plate grid matching actual printer limits
export function SimpleBuildPlate({ printerLimits, materialType }) {
  const limits = printerLimits?.[materialType];
  if (!limits) return null;

  const W = limits.max_x;
  const D = limits.max_y;
  const H = limits.max_z;

  const gridGeometry = React.useMemo(() => {
    const step = 10;
    const hw = W / 2, hd = D / 2;
    const pts = [];
    for (let y = -hd; y <= hd + 0.001; y += step) {
      pts.push(-hw, y, 0,  hw, y, 0);
    }
    for (let x = -hw; x <= hw + 0.001; x += step) {
      pts.push(x, -hd, 0,  x, hd, 0);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
    return geo;
  }, [W, D]);

  const borderGeometry = React.useMemo(() => {
    const hw = W / 2, hd = D / 2;
    const pts = [
      -hw, -hd, 0,   hw, -hd, 0,
       hw, -hd, 0,   hw,  hd, 0,
       hw,  hd, 0,  -hw,  hd, 0,
      -hw,  hd, 0,  -hw, -hd, 0,
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
    return geo;
  }, [W, D]);

  const boxEdges = React.useMemo(() => {
    return new THREE.EdgesGeometry(new THREE.BoxGeometry(W, D, H));
  }, [W, D, H]);

  return (
    <group>
      <lineSegments geometry={gridGeometry}>
        <lineBasicMaterial color="#d1d5db" transparent opacity={0.9} />
      </lineSegments>
      <lineSegments geometry={borderGeometry}>
        <lineBasicMaterial color="#6b7280" />
      </lineSegments>
      <lineSegments geometry={boxEdges} position={[0, 0, H / 2]}>
        <lineBasicMaterial color="#3b82f6" transparent opacity={0.2} />
      </lineSegments>
    </group>
  );
}

// STL Mesh Component
export function SimpleSTLMesh({ stlUrl, scale = 1, selectedColor }) {
  const [geometry, setGeometry] = React.useState(null);

  React.useEffect(() => {
    if (!stlUrl) return;
    let cancelled = false;
    const loader = new STLLoader();
    loader.load(
      stlUrl,
      (geo) => {
        if (cancelled) { geo.dispose(); return; }
        geo.computeBoundingBox();
        const bb = geo.boundingBox;
        const sx = bb.max.x - bb.min.x;
        const sy = bb.max.y - bb.min.y;
        const sz = bb.max.z - bb.min.z;
        if (sy <= sx && sy <= sz) {
          geo.rotateX(Math.PI / 2);
        } else if (sx <= sy && sx <= sz) {
          geo.rotateY(-Math.PI / 2);
        }
        geo.computeVertexNormals();
        geo.computeBoundingBox();
        const center = new THREE.Vector3();
        geo.boundingBox.getCenter(center);
        const minZ = geo.boundingBox.min.z;
        geo.translate(-center.x, -center.y, -minZ + 0.2);
        setGeometry(geo);
      },
      undefined,
      (err) => console.error('STL load error:', err)
    );
    return () => { cancelled = true; };
  }, [stlUrl]);

  const TRANSPARENT_COLORS = new Set(["Transparent (klar)", "Transparent", "Klar"]);

  const modelColor = React.useMemo(() => {
    const colorMap = {
      "Schwarz": "#111827", "Weiß": "#ffffff", "Grau": "#808080",
      "Rot": "#dc2626", "Blau": "#2563eb", "Grün": "#16a34a",
      "Gelb": "#eab308", "Orange": "#ea580c", "Transparent (klar)": "#ffffff",
      "Transparent": "#ffffff", "Natur": "#f59e0b", "Hellgrau": "#9ca3af",
      "Beige": "#e8d5a3", "Klar": "#ffffff"
    };
    return colorMap[selectedColor] || "#f97316";
  }, [selectedColor]);

  const isTransparent = TRANSPARENT_COLORS.has(selectedColor);

  if (!geometry) return null;

  return (
    <mesh geometry={geometry} scale={[scale, scale, scale]}>
      <meshStandardMaterial
        color={modelColor}
        roughness={isTransparent ? 0.05 : 0.4}
        metalness={isTransparent ? 0.1 : 0.0}
        transparent={isTransparent}
        opacity={isTransparent ? 0.22 : 1.0}
        side={THREE.DoubleSide}
        flatShading={false}
      />
    </mesh>
  );
}
